import React, {useEffect, useState} from "react";
import {storageFunctions} from "./storage";
import {StyleSheet, View} from "react-native";
import Baran from "./themes/baran/Baran";
import {FetchEvents} from "./rest/RequestGroup";
import {Menu} from "./rest/requests/Menu";
import {Auth} from "./rest/requests/Auth";
import AppConfigContext, {AppConfigContextType, SnackBarData} from "./contexts/AppConfigContext";
import {Texts} from "./texts";
import {ProgressViewContext} from "./contexts/ProgressViewContext";
import {OrderType} from "./types";
import * as Navigation from "./refrences/RootNavigation";
import {CartProvider} from "./contexts/CartContext";
import {OrderFunctions} from "./rest/requests/OrderFunctions";
import {OrderProvider} from "./contexts/OrderContext";

import * as serviceWorker from "./src/serviceWorkerRegistration";
import {initiatePusherBroadcast} from "./src/pusherScripts";
import {ServiceProvider} from "./contexts/ServicesContext";
import {redirectToMeezbanLanding} from "./helpers";
import {initiateInstallPrompt} from "./src/PWAInstallPrompt";
import {AppFunctionsProvider} from "./contexts/AppFunctionsContext";
import Shayan from "./themes/shayan/Shayan";

export default function App() {

    ////////////// THEME
    const [colorScheme, setColorScheme] = useState<"light" | "dark">("light");
    // todo: uncomment this
    // useEffect(() => {
    //     storageFunctions.getData("scheme").then(storageTheme => {
    //         if (storageTheme == null)
    //             storageFunctions.saveData("scheme", getColorScheme() as string).then(r => console.log(r));
    //         else
    //             setColorScheme(storageTheme as "light"|"dark");
    //     })
    // });
    // let isDark = colorScheme == "dark";

    ////////////// COLOR
    const [themeColor, setThemeColor] = useState<string>("#4169E1");
    const [themeName, setThemeName] = useState<string>("baran");

    ////////////// LANGUAGE
    type langType = "fa" | "en";
    const [lang, setLang] = useState<langType>("fa");
    useEffect(() => {
        storageFunctions.getData("lang").then((storageLang) => {
            if (storageLang == null)
                storageFunctions.saveData("lang", "fa");
            else
                setLang(storageLang as langType);
        });
    });
    let isPersian = lang == "fa";

    //////////////// SNACKBAR
    const [snackBarData, setSnackBarData] = useState<SnackBarData>(null);

    //////////////// CURRENT PAGE HEADER
    const [pageTitle, setPageTitle] = useState<string>("");
    const [pageHasHeader, setPageHasHeader] = useState<boolean>(false);

    /////////////// FETCH TOOLS CONTEXT
    const [showProgress, setShowProgress] = useState(false);
    const path = window.location.pathname;
    const pathParts: string[] = path.slice(1, path.length).split("/");

    /////////////// HANDLING BRANCH
    const [branch, setBranch] = useState<number>(Number.parseInt(pathParts[0]));
    useEffect(() => {
        try {
            if (Number.isInteger(branch))
                storageFunctions.saveData("branch", branch.toString()).then(() => {
                    setBranch(branch);
                });
        } catch (e) {
        }
    }, [branch]);

    /////////////// HANDLING ORDER TYPE
    const [orderType, setOrderType] = useState<OrderType>(pathParts[1] as OrderType);
    useEffect(() => {
        try {
            if (["outside", "takeaway", "table"].includes(orderType)) {
                storageFunctions.saveData("order_type", orderType).then(() => {
                    setOrderType(orderType as OrderType);
                });
            }
        } catch (e) {
        }
    }, [orderType]);

    if (branch == null || orderType == null) {
        redirectToMeezbanLanding();
        return;
    }

    /////////////// HANDLING TABLE ID
    const [tableId, setTableId] = useState<number>(Number.parseInt(pathParts[2]));
    useEffect(() => {
        try {
            if (Number.isInteger(tableId))
                storageFunctions.saveData("table_id", tableId.toString()).then(() => {
                    setTableId(tableId);
                });
        } catch (e) {
            console.error(e);
        }
    }, [tableId]);

    useEffect(() => {
        initiatePusherBroadcast();
        initiateInstallPrompt().then(e => {
            Navigation.navigate("PWAInstallPrompt", {
                event: e,
                origin: "browser"
            });
        });
    }, []);

    // Put browser back button to a loop
    // useEffect(() => {
    //     window.addEventListener('load', function() {
    //         window.history.pushState({  }, '')
    //     });
    //     window.addEventListener('popstate', function(event) {
    //         Navigation.goBack();
    //     });
    // }, []);

    ////////////////////////////////// MAIN STYLING
    const lStyle = StyleSheet.create({
        itemContainer: {
            flex: 1,
            writingDirection: isPersian ? "rtl" : "ltr", maxHeight: "100%",
            overflow: "hidden"
        }
    });

    //////////////////////// DEFAULT FETCH EVENTS
    const defaultFetchEvents: FetchEvents = {
        onStart: () => {
            setShowProgress(true);
        },
        onResponse: (jRes) => {
            setShowProgress(false);
        },
        onError: (err) => {
            if (err == "Unauthenticated.") {
                // Todo: Token is expired or not working for any reason. Replace with something more stable
                Navigation.navigate("Login");
            }
            appContextConfig.snackBarData?.events?.error(err);
        },
        onSuccess: (jData) => {
            // todo remove this
            appContextConfig.snackBarData?.events?.success(jData);
        },
        onPromiseReject: (err) => {
        }
    };

    /////////////////////////// CONFIGURE APP CONTEXT
    const appContextConfig: AppConfigContextType = {
        lang: lang,
        texts: Texts.instance(lang as "fa" | "en"),
        theme: colorScheme,
        color: themeColor,
        fetch: {
            Menu: new Menu({
                defaultEvents: defaultFetchEvents,
                branchId: branch,
                orderType: orderType,
                tableId: tableId
            }),
            Auth: new Auth({defaultEvents: defaultFetchEvents}),
            Order: new OrderFunctions({
                defaultEvents: defaultFetchEvents,
                branchId: branch,
                orderType: orderType,
                tableId: tableId
            })
        },
        branch: branch,
        tableId: tableId,
        orderType: orderType,
        currentPageTitle: pageTitle,
        pageHasHeader: pageHasHeader,
        setThemeColor: (c) => setThemeColor(c),
        setThemeName: (c) => setThemeName(c),
        setIfPageHasHeader: (b) => {
            setPageHasHeader(b)
        },
        setPageTitle: (t: string) => {
            setPageTitle(t);
        },
        snackBarData: {
            data: snackBarData,
            events: {
                success: (n: string) => setSnackBarData({
                    text: n,
                    color: "green",
                    icon: ""
                }),
                error: (n: string) => setSnackBarData({
                    text: n,
                    color: "red",
                    icon: ""
                }),
                info: (n: string) => setSnackBarData({
                    text: n,
                    color: "yellow",
                    icon: ""
                }),
            }
        }
    };

    let themeComponent ;

    switch (themeName) {
        case "shayan":
            themeComponent = <Shayan/>
            break;
        case "baran":
        default:
            themeComponent = <Baran/>
            break;
    }

    return (
        <AppConfigContext.Provider value={appContextConfig}>
            <ProgressViewContext.Provider value={{show: showProgress}}>
                <OrderProvider>
                    <CartProvider>
                        <ServiceProvider>
                            <AppFunctionsProvider>
                                <View style={lStyle.itemContainer}>
                                    {themeComponent}
                                </View>
                            </AppFunctionsProvider>
                        </ServiceProvider>
                    </CartProvider>
                </OrderProvider>
            </ProgressViewContext.Provider>
        </AppConfigContext.Provider>
    );
}

serviceWorker.register();
